import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  IconButton,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CategoryIcon from '@mui/icons-material/Category';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { postData, updateData, fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';

const CategoryModal = ({ 
  open, 
  onClose, 
  onSave, 
  categoryData = null, 
  mode = 'create', // 'create', 'edit', 'view'
  existingCategories = [] // Add this prop to receive the list of existing categories
}) => {
  const { companyId, userId, token } = useAuth();
  const { logAudit } = useAuditLog();
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
  
  const [formData, setFormData] = useState({
    Name: '',
    GSTRateId: '',
    GSTRate: '',
    HSNCode: ''
  });

  const [errors, setErrors] = useState({});
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateMessage, setDuplicateMessage] = useState('');

  // GST Rate dropdown state
  const [gstRates, setGstRates] = useState([]);
  const [gstRatesLoading, setGstRatesLoading] = useState(false);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 600);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch GST Rates from API
  useEffect(() => {
    if (open && companyId && token) {
      fetchGSTRates();
    }
  }, [open, companyId, token]);

  const fetchGSTRates = async () => {
    setGstRatesLoading(true);
    try {
      const response = await fetchData('GSTRateApi', `/${companyId}`, token);
      if (Array.isArray(response.response_data)) {
        const data = response.response_data;
        setGstRates(data.filter(r => r.IsActive !== false));
      } else {
        setGstRates([]);
      }
    } catch (error) {
      console.error('Error fetching GST rates:', error);
      setGstRates([]);
    } finally {
      setGstRatesLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      console.log('Modal opened with mode:', mode, 'categoryData:', categoryData);
      
      if (categoryData && mode !== 'create') {
        console.log('Loading existing category data for edit/view, ID:', categoryData.Id);
        setFormData({
          Id: categoryData.Id || '',
          Name: categoryData.Name || '',
          GSTRateId: categoryData.GSTRateId || '',
          GSTRate: categoryData.GSTRate || '',
          HSNCode: categoryData.HSNCode || ''
        });
      } else {
        console.log('Creating new category');
        // Reset form for create mode
        setFormData({
          Name: '',
          GSTRateId: '',
          GSTRate: '',
          HSNCode: ''
        });
      }
      setErrors({});
    }
  }, [open, categoryData, mode]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    
    // Validate HSN Code - only allow digits
    if (name === 'HSNCode' && value) {
      const hsnPattern = /^[0-9]*$/;
      if (!hsnPattern.test(value)) {
        return; // Don't update if invalid characters
      }
    }
    
    // Handle numeric input for GST Rate
    if (name === 'GSTRate') {
      // Allow only numbers and decimal point
      const numericValue = value.replace(/[^0-9.]/g, '');
      // Ensure only one decimal point
      const parts = numericValue.split('.');
      const formattedValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : numericValue;
      
      setFormData(prev => ({
        ...prev,
        [name]: formattedValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.Name.trim()) {
      newErrors.Name = 'Category name is required';
    }
    
    if (!formData.GSTRate || formData.GSTRate === '') {
      newErrors.GSTRate = 'VAT Rate is required';
    } else {
      const gstRate = parseFloat(formData.GSTRate);
      if (isNaN(gstRate) || gstRate < 0 || gstRate > 100) {
        newErrors.GSTRate = 'VAT Rate must be between 0 and 100';
      }
    }
    
    if (!formData.HSNCode.trim()) {
      newErrors.HSNCode = 'HSN Code is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const checkDuplicateCategory = () => {
    // Check if a category with the same name exists in the list (case-insensitive)
    const duplicate = existingCategories.find(category => {
      // Skip the current category in edit mode
      if (mode === 'edit' && categoryData && category.Id === categoryData.Id) {
        return false;
      }
      
      // Check if name matches (case-insensitive)
      const nameMatch = category.Name?.trim().toLowerCase() === formData.Name.trim().toLowerCase();
      
      return nameMatch;
    });
    
    return duplicate;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    // Check for duplicate category
    const duplicate = checkDuplicateCategory();
    if (duplicate) {
      setDuplicateMessage(`A category with the name "${formData.Name}" already exists.`);
      setDuplicateDialogOpen(true);
      return;
    }

    setLoading(true);
    try {
      const dataToSend = {
        Name: formData.Name,
        GSTRateId: parseInt(formData.GSTRateId) || 0,
        GSTRate: parseFloat(formData.GSTRate) || 0,
        HSNCode: formData.HSNCode,
        CompanyId: companyId,
      };

      if (mode === 'edit') {
        // Validate that we have the category ID
        if (!categoryData || !categoryData.Id) {
          console.error('Cannot update: Missing category ID');
          alert('Error: Cannot update category - missing ID');
          return;
        }
        dataToSend.Id = categoryData.Id;
        // For edit mode, add modification fields but don't include ID in body (it's in URL)
        dataToSend.ModifiedBy = userId;
        dataToSend.ModifiedDate = new Date();
        // Keep original creation data if available
        if (categoryData.CreatedBy) dataToSend.CreatedBy = categoryData.CreatedBy;
        if (categoryData.CreatedDate) dataToSend.CreatedDate = categoryData.CreatedDate;
        
        console.log('Updating category with ID:', categoryData.Id);
        console.log('Update data being sent:', dataToSend);
        console.log('API call: PUT /CategoryApi/' + categoryData.Id);
        
        const response = await updateData('CategoryApi', categoryData.Id, dataToSend, token);
      logAudit(AUDIT_ACTION.UPDATE, 'CategoryApi', categoryData.Id, { ...selectedItem }, { ...formData });
        console.log('Update response:', response);
      } else {
        // For create mode, add creation fields
        dataToSend.CreatedBy = userId;
        dataToSend.CreatedDate = new Date();
        
        console.log('Creating new category');
        console.log('Create data being sent:', dataToSend);
        console.log('API call: POST /CategoryApi');
        
        const response = await postData('CategoryApi', '', dataToSend, token);
      logAudit(AUDIT_ACTION.CREATE, 'CategoryApi', response?.Id || 0, null, { Name: formData.Name || '' });
        console.log('Create response:', response);
      }

      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving category:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    switch (mode) {
      case 'create': return 'New Category';
      case 'edit': return 'Edit Category';
      case 'view': return 'Category Details';
      default: return 'Category';
    }
  };

  const isReadOnly = mode === 'view';

  // Common input styles
  const inputStyle = { borderRadius: '6px', fontSize: '13px', height: '32px' };
  const labelStyle = { fontSize: '11px' };
  const helperStyle = { fontSize: '9px', margin: '1px 0 0 0' };

  return (
    <>
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        style: {
          borderRadius: isMobile ? '0' : '12px',
          background: 'linear-gradient(135deg, #1d2445ff 0%, #1b0b2bff 100%)',
          padding: '2px',
          margin: isMobile ? '0' : '8px'
        }
      }}
    >
      <Box style={{
        background: 'white',
        borderRadius: isMobile ? '0' : '10px',
        overflow: 'hidden',
        height: isMobile ? '100vh' : 'auto'
      }}>
        <DialogTitle style={{
          background: 'linear-gradient(135deg, #121627ff 0%, #2c1b3cff 100%)',
          color: 'white',
          padding: '8px 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: 'auto'
        }}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <CategoryIcon style={{ fontSize: '18px' }} />
            <Typography variant="h6" style={{ fontWeight: '600', fontSize: '14px' }}>
              {getTitle()}
            </Typography>
          </Box>
          <IconButton 
            onClick={onClose}
            style={{ color: 'white', padding: '2px' }}
            size="small"
          >
            <CloseIcon style={{ fontSize: '18px' }} />
          </IconButton>
        </DialogTitle>

        <DialogContent style={{ 
          padding: '8px 12px', 
          paddingBottom: '4px'
        }}>
          <Typography variant="subtitle2" style={{ 
            color: '#666', 
            marginBottom: '6px',
            fontSize: '10px',
            lineHeight: '1.2'
          }}>
            {mode === 'create' ? 'Add a new category to your system' : 
             mode === 'edit' ? 'Update category information' : 
             'View category details'}
          </Typography>

          <Grid container spacing={1}>
            {/* Category Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" style={{ 
                fontWeight: '600', 
                marginBottom: '3px',
                color: '#2d3748',
                fontSize: '12px'
              }}>
                Category Information
              </Typography>
            </Grid>

            {/* Category Name */}
            <Grid item xs={12}>
              <TextField
                name="Name"
                label="Category Name *"
                value={formData.Name}
                onChange={handleChange}
                fullWidth
                size="small"
                error={!!errors.Name}
                helperText={errors.Name}
                disabled={isReadOnly}
                placeholder="Enter category name"
                inputProps={{ maxLength: 150 }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
                FormHelperTextProps={{ style: helperStyle }}
              />
            </Grid>

            {/* GST Rate and HSN Code - Same Row */}
            <Grid item xs={12} sm={6}>
              <TextField
                select
                name="GSTRateId"
                label="GST Rate (%) *"
                value={formData.GSTRateId}
                onChange={(e) => {
                  const selectedId = e.target.value;
                  const selectedRate = gstRates.find(r => String(r.Id) === String(selectedId));
                  setFormData(prev => ({
                    ...prev,
                    GSTRateId: selectedId,
                    GSTRate: selectedRate ? selectedRate.Rate : ''
                  }));
                  if (errors.GSTRate) {
                    setErrors(prev => ({ ...prev, GSTRate: '' }));
                  }
                }}
                fullWidth
                size="small"
                error={!!errors.GSTRate}
                helperText={errors.GSTRate}
                disabled={isReadOnly || gstRatesLoading}
                SelectProps={{ native: true }}
                InputProps={{ 
                  style: inputStyle,
                  endAdornment: gstRatesLoading ? <CircularProgress size={14} /> : null
                }}
                InputLabelProps={{ style: labelStyle, shrink: true }}
                FormHelperTextProps={{ style: helperStyle }}
              >
                <option value="">Select GST Rate</option>
                {gstRates.map(rate => (
                  <option key={rate.Id} value={rate.Id}>{rate.Rate}%</option>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="HSNCode"
                label="HSN Code *"
                value={formData.HSNCode}
                onChange={handleChange}
                fullWidth
                size="small"
                error={!!errors.HSNCode}
                helperText={errors.HSNCode}
                disabled={isReadOnly}
                placeholder="Enter HSN code"
                inputProps={{ maxLength: 10 }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
                FormHelperTextProps={{ style: helperStyle }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions style={{ 
          padding: '8px 12px',
          background: '#f8f9fa',
          borderTop: '1px solid #e9ecef',
          gap: '6px',
          minHeight: 'auto'
        }}>
          <Button 
            onClick={onClose}
            style={{
              color: '#6c757d',
              textTransform: 'none',
              fontWeight: '500',
              fontSize: '12px',
              padding: '4px 8px'
            }}
          >
            Cancel
          </Button>
          {!isReadOnly && (
            <Button 
              onClick={handleSave}
              disabled={loading}
              variant="contained"
              style={{
                background: 'linear-gradient(135deg, #53dac3ff 0%, #45a359ff 100%)',
                color: 'white',
                textTransform: 'none',
                fontWeight: '600',
                borderRadius: '4px',
                padding: '4px 12px',
                fontSize: '12px'
              }}
            >
              {loading ? 'Saving...' : mode === 'create' ? 'Add Category' : 'Update Category'}
            </Button>
          )}
        </DialogActions>
      </Box>
    </Dialog>

    {/* Duplicate Warning Dialog */}
    <Dialog
      open={duplicateDialogOpen}
      onClose={() => setDuplicateDialogOpen(false)}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: '12px',
          padding: '8px'
        }
      }}
    >
      <DialogContent style={{ textAlign: 'center', padding: '24px' }}>
        <WarningAmberIcon 
          style={{ 
            fontSize: '64px', 
            color: '#ff9800',
            marginBottom: '16px'
          }} 
        />
        <Typography 
          variant="h6" 
          style={{ 
            fontWeight: '600', 
            marginBottom: '12px',
            color: '#2d3748',
            fontSize: '16px'
          }}
        >
          Duplicate Category Found
        </Typography>
        <Typography 
          style={{ 
            color: '#666',
            fontSize: '14px',
            lineHeight: '1.5'
          }}
        >
          {duplicateMessage}
        </Typography>
      </DialogContent>
      <DialogActions style={{ padding: '0 24px 24px 24px', justifyContent: 'center' }}>
        <Button
          onClick={() => setDuplicateDialogOpen(false)}
          variant="contained"
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            textTransform: 'none',
            fontWeight: '600',
            borderRadius: '8px',
            padding: '8px 32px',
            fontSize: '14px'
          }}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
  </>
  );
};

export default CategoryModal;
